import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
	COLLECTION_CONFIG_SCOPE,
	COLLECTIONS_QUERY_KEY,
	invalidateCollectionName,
	removeDeletedCollection,
} from '@utils/collectionQueries';

import { createCollection, deleteCollection, getCollections, updateCollection } from '@utils/api';

/**
 * React Query hooks for object-picker collections.
 *
 * Reads go through `useCollections`; writes (create/update/delete) invalidate
 * the shared `['collections']` key so the list refetches after a mutation.
 * Backed by the v2 collections API (see `@utils/api/collections`).
 *
 * An update shares `COLLECTION_CONFIG_SCOPE` with the layers panel's writes,
 * which stops two saves against the same document from overlapping. It also
 * clears the other caches that render the collection's name — see
 * `invalidateCollectionName`.
 */

export { COLLECTIONS_QUERY_KEY };

// Shared by create and update, so a page can ask `useIsMutating` whether a save is in flight.
export const COLLECTION_SAVE_KEY = ['collections', 'save'];

export const useCollections = () =>
	useQuery({
		queryKey: COLLECTIONS_QUERY_KEY,
		queryFn: getCollections,
	});

export const useCreateCollection = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: COLLECTION_SAVE_KEY,
		// payload: { name, description?, objects: [{ key, label }] }
		mutationFn: createCollection,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: COLLECTIONS_QUERY_KEY }),
	});
};

/**
 * The collection is fixed for the lifetime of the hook because the scope has to
 * be: React Query resolves it when the mutation is created, not per call.
 *
 * @param {string} collectionUuid - the collection being edited
 */
export const useUpdateCollection = collectionUuid => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: COLLECTION_SAVE_KEY,
		scope: COLLECTION_CONFIG_SCOPE(collectionUuid),
		// variables: the update payload
		mutationFn: payload => updateCollection(collectionUuid, payload),
		// A rename has to reach every surface that shows the name, not just the
		// collections list.
		onSuccess: () => invalidateCollectionName(queryClient, collectionUuid),
	});
};

/**
 * Deleting clears the collection's own caches as well as the lists that named
 * it — see `removeDeletedCollection`. Clearing a *selection* that pointed at it
 * is the caller's job: this hook has no view of the project's selected
 * collection.
 *
 * TODO: no caller yet — the Collections page's delete should use it. When the
 * deleted collection is the one the map shows, that caller must dispatch
 * `setCollectionUuid` with `null` and remove the `collection_uuid` search param
 * (updater form, `{ replace: true }` so Back can't restore a dead uuid);
 * otherwise the layers panel keeps rendering the deleted collection.
 */
export const useDeleteCollection = () => {
	const queryClient = useQueryClient();
	return useMutation({
		// variables: the collection id
		mutationFn: deleteCollection,
		onSuccess: (_data, collectionUuid) => removeDeletedCollection(queryClient, collectionUuid),
	});
};
